import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {PaymentDetails} from 'src/types/referrer-portal';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {useInflight} from 'src/hooks/redux';
import {selectPaymentDetails} from 'src/selectors/referrer-portal';
import classify from 'src/utils/classify';
import {paymentInitialData} from 'src/utils/referrer-portal';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import {
  getPaymentDetails,
  updatePaymentDetails,
} from 'src/action-creators/referrer-portal/earnings';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardActions,
  Button,
  CircularLoader,
} from '@spaced-out/ui-design-system/lib/components/index';
import PaymentCard, {
  PaymentRadioSelect,
} from 'src/components/referrer-portal/earnings/edit-payment-card';
import ReferralDetailRow from 'src/components/referral-v2/table/referral-details-row';
import EarningTabs from 'src/components/referrer-portal/earnings/earnings-tabs';
import css from './earnings.css';


const Payments = () => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const paymentDetails: PaymentDetails[] | null | undefined =
    useSelector(selectPaymentDetails);

  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState(new Map());
  const [paymentFormData, setPaymentFormData] =
    React.useState<PaymentDetails>(paymentInitialData);

  const validate = () => {
    const newErrors = new Map();
    let requiredFields;

    if (paymentFormData.method === 'paypal') {
      requiredFields = ['paypal_id'];
    } else {
      requiredFields = [
        'account_number',
        'bank_name',
        'name',
        'routing_number',
        'swift_code',
      ];
    }

    requiredFields.forEach((field) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; bank_name: string; account_number: string; routing_number: string; swift_code: string; paypal_id: string; }'.
      const value = paymentFormData.details[field];
      if (!value) {
        newErrors.set(field, t('Field_is_required', 'Field is required'));
      }
    });

    setErrors(newErrors);
    return newErrors;
  };

  const handleOnChangePayment = (
    field: string,
    value: 'paypal' | 'bank' | string,
  ) => {
    let updatedData: PaymentDetails = paymentFormData;
    if (field === 'method' && value) {
      updatedData = {
        ...paymentInitialData,
        [field]: value,
      };
      setPaymentFormData(updatedData);
    } else {
      updatedData = {
        ...updatedData,
        details: {...updatedData.details, [field]: value},
      };
      setPaymentFormData(updatedData);
    }
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error.size > 0) {
      return;
    }

    const {
      method,
      details: {
        paypal_id,
        bank_name,
        account_number,
        name,
        routing_number,
        swift_code,
      },
    } = paymentFormData;

    const payload = {
      ...paymentFormData,
      is_preferred: true,
      details:
        method === 'paypal'
          ? {paypal_id}
          : {bank_name, account_number, name, routing_number, swift_code},
    } as const;

    setIsSubmitting(true);
    // @ts-expect-error - TS2345 - Argument of type '{ readonly is_preferred: true; readonly details: { paypal_id: string; bank_name?: undefined; account_number?: undefined; name?: undefined; routing_number?: undefined; swift_code?: undefined; } | { bank_name: string; ... 4 more ...; paypal_id?: undefined; }; readonly method: "paypal" | "bank" | string; }' is not assignable to parameter of type 'PaymentDetails'.
    dispatch(updatePaymentDetails(payload))
      .then((response) => {
        //@ts-expect-error
        if (response) {
          setIsEdit(false);
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const isPaymentDetailsLoading = useInflight(getPaymentDetails());

  const paypalDetails = paymentDetails?.find(
    (details: PaymentDetails) => details.method === 'paypal',
  );

  const bankDetails = paymentDetails?.find(
    (details: PaymentDetails) => details.method === 'bank',
  );

  const isPaypal = paypalDetails?.is_preferred;

  const isReferrerPortalEnv = isReferrerPortal();

  useAsyncDependency(() => {
    return dispatch(getPaymentDetails());
  }, []);

  React.useEffect(() => {
    if (paymentDetails) {
      const data = isPaypal ? paypalDetails : bankDetails;
      setPaymentFormData(data || paymentInitialData);
    }
  }, [paymentDetails, isEdit]);

  return (
    <div
      className={
        isReferrerPortalEnv
          ? css.referrerPortalPaymentContainer
          : css.paymentContainer
      }
    >
      {!isReferrerPortalEnv && <EarningTabs className={css.earningTabs} />}
      {isPaymentDetailsLoading ? (
        <CircularLoader className={css.loader} />
      ) : isEdit ? (
        <PaymentCard
          errors={errors}
          paymentData={paymentFormData}
          handleOnChange={handleOnChangePayment}
          footer={
            <CardFooter>
              <CardActions className={css.defaultGap}>
                <Button
                  type="ghost"
                  onClick={() => {
                    setIsEdit(false);
                    setPaymentFormData(paymentInitialData);
                  }}
                >
                  {t('Cancel', 'Cancel')}
                </Button>
                <Button
                  disabled={isSubmitting}
                  isLoading={false}
                  onClick={handleSubmit}
                >
                  {t('Submit', 'Submit')}
                </Button>
              </CardActions>
            </CardFooter>
          }
        />
      ) : (
        <>
          <PaymentRadioSelect value={isPaypal ? 'paypal' : 'bank'} />
          <Card classNames={{wrapper: css.paymentFormCard}}>
            <CardHeader>
              {isPaypal
                ? t('PayPal_Details', 'PayPal Details')
                : t('Bank_Details', 'Bank Details')}
            </CardHeader>
            <CardContent className={classify({[css.bankDetails]: !isPaypal})}>
              {isPaypal ? (
                <ReferralDetailRow
                  label={t('PayPal_ID', 'PayPal ID')}
                  value={paypalDetails?.details.paypal_id}
                />
              ) : (
                <>
                  <ReferralDetailRow
                    value={bankDetails?.details.name}
                    label={t(
                      'Name_as_per_Bank_Account',
                      'Name as per Bank Account',
                    )}
                  />
                  <ReferralDetailRow
                    value={bankDetails?.details.bank_name ? '**********' : null}
                    label={t('Bank_Name', 'Bank Name')}
                  />
                  <ReferralDetailRow
                    value={
                      bankDetails?.details.account_number ? '**********' : null
                    }
                    label={t('Account_Number', 'Account Number')}
                  />
                  <ReferralDetailRow
                    value={
                      bankDetails?.details.routing_number ? '**********' : null
                    }
                    label={t('Routing_Number', 'Routing Number')}
                  />
                  <ReferralDetailRow
                    value={
                      bankDetails?.details.swift_code ? '**********' : null
                    }
                    label={t('SWIFT_code', 'SWIFT code')}
                  />
                </>
              )}
            </CardContent>
            <CardFooter>
              <CardActions>
                <Button
                  iconRightName="pen-to-square"
                  onClick={() => setIsEdit(true)}
                >
                  {t('Edit_Details', 'Edit Details')}
                </Button>
              </CardActions>
            </CardFooter>
          </Card>
        </>
      )}
    </div>
  );
};

export default Payments;
